import CalendarIcon from '@heroicons/react/outline/CalendarIcon'
import ChatAltIcon from '@heroicons/react/outline/ChatAltIcon'
import HeartIcon from '@heroicons/react/outline/HeartIcon'
import IdentificationIcon from '@heroicons/react/outline/IdentificationIcon'
import InformationCircleIcon from '@heroicons/react/outline/InformationCircleIcon'
import MenuAlt1Icon from '@heroicons/react/outline/MenuAlt1Icon'
import MenuAlt2Icon from '@heroicons/react/outline/MenuAlt2Icon'
import NewspaperIcon from '@heroicons/react/outline/NewspaperIcon'
import PhotographIcon from '@heroicons/react/outline/PhotographIcon'
import ShareIcon from '@heroicons/react/outline/ShareIcon'
import TagIcon from '@heroicons/react/outline/TagIcon'
import ThumbUpIcon from '@heroicons/react/outline/ThumbUpIcon'
import UserCircleIcon from '@heroicons/react/outline/UserCircleIcon'

import {
  CustomFieldType,
  PostListOrderByEnum,
} from '@tribeplatform/gql-client/types'
import type { I18nShape } from '@tribeplatform/react-components/i18n'

import type { StaticField } from '../Views/types'
import { StaticFieldTypes } from '../Views/types'
import type { PostsBlockProps, PostsBlockView } from './types'

export const genericViewFactory: (intl: I18nShape) => PostsBlockView = ({
  $t,
}) => ({
  id: '',
  name: $t({
    defaultMessage: 'Default',
    id: 'Generics.Default',
  }),
  filterBy: [],
  sort: PostListOrderByEnum.publishedAt,
  reverseSort: true,
  style: 'list',
  gallerySize: 'medium',
  showDivider: true,
  limit: 10,
  fields: [],
  linkTo: '',
  showMore: false,
  spaceId: '{{ spaceId }}',
})

export const staticFieldsFactory: (intl: I18nShape) => StaticField[] = ({
  $t,
}) => [
  {
    id: 'postInfoBar',
    label: $t({
      defaultMessage: 'Post info bar',
      id: 'PostsBlocks.FieldLabels.PostInfoBar',
    }),
    enabled: false,
    locked: false,
    styles: ['card'],
    settings: { showBackToSpaceButton: true },
    isStatic: true,
    icon: <InformationCircleIcon />,
  },
  {
    id: 'cardMemberInfo',
    label: $t({
      defaultMessage: '{MEMBER_CC} info',
      id: 'PostsBlocks.FieldLabels.MemberInfo',
    }),
    enabled: false,
    locked: false,
    styles: ['card'],
    settings: {
      fullWidth: true,
      showSpaceName: true,
      showPostOptions: false,
    },
    isStatic: true,
    icon: <IdentificationIcon />,
  },
  {
    id: 'galleryBanner',
    label: $t({
      defaultMessage: 'Post banner',
      id: 'PostsBlocks.FieldLabels.PostBanner',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      bannerSource: 'post',
      aspectRatio: 'aspect-video',
    },
    isStatic: true,
    icon: <PhotographIcon />,
    default: '/images/banners/banner-placeholder.jpg',
  },
  {
    id: 'cardBanner',
    label: $t({
      defaultMessage: 'Post banner',
      id: 'PostsBlocks.FieldLabels.PostBanner',
    }),
    enabled: false,
    locked: true,
    styles: ['card'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      bannerSource: 'post',
      aspectRatio: 'aspect-[4/1]',
    },
    isStatic: true,
    icon: <PhotographIcon />,
    default: '/images/banners/banner-placeholder.jpg',
  },
  {
    id: 'listAvatar',
    label: $t({
      defaultMessage: '{MEMBER_CC} avatar',
      id: 'PostsBlocks.FieldLabels.MemberAvatar',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    type: StaticFieldTypes.avatar,
    settings: {
      avatarSize: 'lg',
      bannerSource: 'member',
    },
    isStatic: true,
    icon: <UserCircleIcon />,
  },
  {
    id: 'listPostSummary',
    label: $t({
      defaultMessage: 'Post summary',
      id: 'PostsBlocks.FieldLabels.PostSummary',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    settings: {
      stretchX: true,
    },
    isStatic: true,
    icon: <NewspaperIcon />,
    fields: [
      {
        id: 'listPostTitle',
        label: $t({
          defaultMessage: 'Post title',
          id: 'PostsBlocks.FieldLabels.PostTitle',
        }),
        enabled: true,
        locked: false,
        type: StaticFieldTypes.title,
        settings: {
          titleSize: 'md',
        },
        isStatic: true,
        icon: <MenuAlt2Icon />,
        default: 'Lorem sunt excepteur aliquip',
      },
      {
        id: 'listPostContent',
        label: $t({
          defaultMessage: 'Post content',
          id: 'PostsBlocks.FieldLabels.PostContent',
        }),
        enabled: true,
        locked: false,
        settings: {},
        isStatic: true,
        icon: <MenuAlt1Icon />,
        default:
          '<p>Commodo <b>magna esse</b> ea in in. Minim <i>occaecat</i> aute <a>veniam pariatur</a> laboris veniam.</p>',
      },
      {
        id: 'listTags',
        label: $t({
          defaultMessage: '{TAGS_CC}',
          id: 'Generics.Tags',
        }),
        enabled: true,
        locked: false,
        settings: {},
        isStatic: true,
        icon: <TagIcon />,
      },
    ],
  },
  {
    id: 'galleryPostTitle',
    label: $t({
      defaultMessage: 'Post title',
      id: 'PostsBlocks.FieldLabels.PostTitle',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel', 'card'],
    type: StaticFieldTypes.title,
    settings: {
      fullWidth: true,
      titleSize: 'md',
    },
    isStatic: true,
    icon: <MenuAlt2Icon />,
    default: 'Lorem sunt excepteur aliquip',
  },
  {
    id: 'galleryPostContent',
    label: $t({
      defaultMessage: 'Post content',
      id: 'PostsBlocks.FieldLabels.PostContent',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    settings: {
      fullWidth: true,
    },
    isStatic: true,
    icon: <MenuAlt1Icon />,
    default:
      '<p>Commodo <b>magna esse</b> ea in in. Minim <i>occaecat</i> aute <a>veniam pariatur</a> laboris veniam.</p>',
  },
  {
    id: 'cardPostContent',
    label: $t({
      defaultMessage: 'Post content',
      id: 'PostsBlocks.FieldLabels.PostContent',
    }),
    enabled: true,
    locked: false,
    styles: ['card'],
    settings: {
      fullWidth: true,
      displayStyle: 'expandable',
      truncateSize: 'md',
    },
    isStatic: true,
    icon: <MenuAlt1Icon />,
    default:
      '<p>Commodo <b>magna esse</b> ea in in. Minim <i>occaecat</i> aute <a>veniam pariatur</a> laboris veniam.</p>',
  },
  {
    id: 'tags',
    label: $t({
      defaultMessage: '{TAGS_CC}',
      id: 'Generics.Tags',
    }),
    enabled: false,
    locked: false,
    settings: { fullWidth: true },
    isStatic: true,
    icon: <TagIcon />,
  },
  {
    id: 'reactButton',
    label: $t({
      defaultMessage: 'React button',
      id: 'PostsBlocks.FieldLabels.ReactButton',
    }),
    enabled: false,
    locked: false,
    type: StaticFieldTypes.button,
    styles: ['carousel', 'gallery', 'list'],
    settings: { buttonVariant: 'iconText' },
    isStatic: true,
    icon: <HeartIcon />,
  },
  {
    id: 'reactionCount',
    label: $t({
      defaultMessage: 'Reaction count',
      id: 'PostsBlocks.FieldLabels.ReactionCount',
    }),
    enabled: false,
    locked: false,
    settings: {},
    isStatic: true,
    styles: ['carousel', 'gallery', 'list'],
    icon: <ThumbUpIcon />,
  },
  {
    id: 'cardPostInfoBar',
    label: $t({
      defaultMessage: 'Post activity bar',
      id: 'PostsBlocks.FieldLabels.PostActivityBar',
    }),
    enabled: false,
    locked: false,
    settings: {
      showReactionCount: true,
      showReplyCount: true,
      showFollowCount: true,
      fullWidth: true,
    },
    isStatic: true,
    styles: ['card'],
    icon: <InformationCircleIcon />,
  },
  {
    id: 'replyCount',
    label: $t({
      defaultMessage: 'Reply count',
      id: 'PostsBlocks.FieldLabels.ReplyCount',
    }),
    enabled: false,
    locked: false,
    settings: {},
    isStatic: true,
    styles: ['carousel', 'gallery', 'list'],
    icon: <ChatAltIcon />,
  },
  {
    id: 'publishDate',
    label: $t({
      defaultMessage: 'Publish date',
      id: 'Generics.PublishDate',
    }),
    enabled: false,
    locked: false,
    type: CustomFieldType.date,
    isStatic: true,
    icon: <CalendarIcon />,
    settings: {
      customFieldSettings: [
        {
          key: 'view',
          value: $t({
            defaultMessage: '"Date"',
            id: 'Generics.PublishDate.Value',
          }),
        },
      ],
    },
  },
  {
    id: 'galleryMemberInfo',
    label: $t({
      defaultMessage: '{MEMBER_CC} info',
      id: 'PostsBlocks.FieldLabels.MemberInfo',
    }),
    enabled: false,
    locked: false,
    styles: ['gallery', 'carousel'],
    settings: {
      fullWidth: true,
      showSpaceName: true,
    },
    isStatic: true,
    icon: <IdentificationIcon />,
  },
  {
    id: 'actionBar',
    label: $t({
      defaultMessage: 'Action bar',
      id: 'PostsBlocks.FieldLabels.ActionBar',
    }),
    enabled: true,
    locked: false,
    type: StaticFieldTypes.button,
    styles: ['card'],
    settings: { buttonVariant: 'iconText', fullWidth: true },
    isStatic: true,
    icon: <ShareIcon />,
  },
  {
    id: 'replyList',
    label: $t({
      defaultMessage: 'Replies list',
      id: 'PostsBlocks.FieldLabels.RepliesList',
    }),
    enabled: false,
    locked: true,
    styles: ['card'],
    settings: { fullWidth: true },
    isStatic: true,
    icon: <ChatAltIcon />,
  },
]

export const PostsConfig: (intl: I18nShape) => {
  defaultProps: PostsBlockProps
  displayName: string
  removable: boolean
  Icon: JSX.Element
} = ({ $t }) => ({
  defaultProps: {
    views: [],
    title: '',
    description: '',
    linkText: '',
    linkUrl: '',
  },
  displayName: $t({ defaultMessage: 'Posts', id: 'Blocks.Posts.DisplayName' }),
  removable: true,
  Icon: <NewspaperIcon />,
})
